<div class="container mx-auto px-4 py-8">
  <div class="mx-auto max-w-3xl">
    <h1 class="mb-6 text-3xl font-bold text-gray-900 dark:text-gray-100">About Summerry</h1>

    <div class="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-900">
      <section aria-labelledby="what-is-summerry">
        <h2 id="what-is-summerry" class="mb-3 text-xl font-semibold text-gray-900 dark:text-gray-100">
          What Is Summerry — and Why You Might Find It Useful
        </h2>
        <p class="mb-4 text-gray-700 dark:text-gray-300">
          Summerry is an AI news digest that delivers automated tech summaries of key developments in artificial
          intelligence, machine learning, and related technologies.
        </p>
        <p class="mb-4 text-gray-700 dark:text-gray-300">
          It's designed to help you quickly navigate the flow of information, see how different sources report the same
          event, and follow multiple trusted media outlets in one place. Our aim is to save time, reduce information
          overload, and provide access to diverse perspectives — with a focus on clarity, structure, and essential
          information.
        </p>
      </section>

      <section aria-labelledby="why-we-started">
        <h2 id="why-we-started" class="mb-3 mt-6 text-xl font-semibold text-gray-900 dark:text-gray-100">
          Why We Started Summerry — and What We're Exploring
        </h2>
        <p class="mb-4 text-gray-700 dark:text-gray-300">
          With the rapid growth and acceleration of AI, staying up to date can feel increasingly complex. News breaks
          constantly, sources are numerous, formats vary — and it's not always easy to make sense of it all. For many
          who follow emerging technologies, having a structured, accessible overview can be valuable.
        </p>
        <p class="mb-4 text-gray-700 dark:text-gray-300">
          At the same time, we saw that existing tools could help automate and streamline information flow. This
          inspired us to explore their potential — and build Summerry: first as an experiment, then as an independent
          service.
        </p>
      </section>

      <section aria-labelledby="how-it-works">
        <h2 id="how-it-works" class="mb-3 mt-6 text-xl font-semibold text-gray-900 dark:text-gray-100">
          How Summerry Works
        </h2>
        <p class="mb-4 text-gray-700 dark:text-gray-300">
          We process articles from trusted sources in AI that meet basic reliability criteria: consistent publishing,
          citation of sources, and transparent editorial policies.
        </p>
        <p class="mb-4 text-gray-700 dark:text-gray-300">
          For each topic, Summerry collects links from multiple outlets — allowing readers to compare interpretations
          and get a broader view of the event.
        </p>
        <p class="mb-4 text-gray-700 dark:text-gray-300">Our algorithms then:</p>
        <ul class="mb-4 ml-6 list-disc text-gray-700 dark:text-gray-300">
          <li>group articles by topic,</li>
          <li>highlight frequently discussed themes,</li>
          <li>verify that events are reported by multiple independent sources,</li>
          <li>select up to five key events per digest.</li>
        </ul>
        <p class="mb-4 text-gray-700 dark:text-gray-300">
          We aggregate articles from a wide range of reliable publications — including
          <a
            href="https://arxiv.org/"
            target="_blank"
            rel="noopener"
            class="underline px-0 text-base text-gray-900 transition-colors hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-400"
            >arXiv</a
          >,
          <a
            href="https://www.technologyreview.com/"
            target="_blank"
            rel="noopener"
            class="underline px-0 text-base text-gray-900 transition-colors hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-400"
            >MIT Technology Review</a
          >,
          <a
            href="https://techcrunch.com/"
            target="_blank"
            rel="noopener"
            class="underline px-0 text-base text-gray-900 transition-colors hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-400"
            >TechCrunch</a
          >,
          <a
            href="https://www.theverge.com/"
            target="_blank"
            rel="noopener"
            class="underline px-0 text-base text-gray-900 transition-colors hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-400"
            >The Verge</a
          >,
          <a
            href="https://www.bbc.com/"
            target="_blank"
            rel="noopener"
            class="underline px-0 text-base text-gray-900 transition-colors hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-400"
            >BBC</a
          >,
          <a
            href="https://www.reuters.com/"
            target="_blank"
            rel="noopener"
            class="underline px-0 text-base text-gray-900 transition-colors hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-400"
            >Reuters</a
          >
          and
          <a
            href="https://www.nytimes.com/"
            target="_blank"
            rel="noopener"
            class="underline px-0 text-base text-gray-900 transition-colors hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-400"
            >The New York Times</a
          >
          — to ensure diverse, verifiable perspectives.
        </p>
        <p class="mb-4 text-gray-700 dark:text-gray-300">
          The site updates regularly. All processes are automated: we design the logic, set up filters and rules — but
          we do not edit content manually.
        </p>
        <p class="mb-4 text-gray-700 dark:text-gray-300">
          Summerry offers a fast, consistent way to stay informed through automated AI reporting.
        </p>
      </section>

      <section aria-labelledby="whats-in-digest">
        <h2 id="whats-in-digest" class="mb-3 mt-6 text-xl font-semibold text-gray-900 dark:text-gray-100">
          What's in Each Digest
        </h2>
        <p class="mb-4 text-gray-700 dark:text-gray-300">
          Each update includes a short headline, a brief description of the event, and links to original sources. We aim
          to preserve the core facts as presented in original reporting, offering minimal interpretation and no added
          commentary.
        </p>
      </section>

      <section aria-labelledby="whats-in-progress">
        <h2 id="whats-in-progress" class="mb-3 mt-6 text-xl font-semibold text-gray-900 dark:text-gray-100">
          What Works Today — and What's Still in Progress
        </h2>
        <p class="mb-4 text-gray-700 dark:text-gray-300">
          Summerry is currently in its MVP phase. We're testing whether this format helps users save time, build trust,
          and feel more oriented in the fast-changing AI landscape. The project's development depends on user needs, the
          pace of change in the field, and how the platform is used.
        </p>
        <p class="mb-4 text-gray-700 dark:text-gray-300">
          We do not assess or alter the accuracy of original reporting. Instead, we prioritize sources known for their
          transparency and consistency.
        </p>
        <p class="mb-4 text-gray-700 dark:text-gray-300">
          Although Summerry relies on AI-generated summaries, we continuously refine our algorithms and editorial
          filters to minimize factual inaccuracies. We're actively developing an internal verification system to improve
          reliability while maintaining full automation.
        </p>
      </section>

      <section aria-labelledby="who-is-it-for">
        <h2 id="who-is-it-for" class="mb-3 mt-6 text-xl font-semibold text-gray-900 dark:text-gray-100">
          Who Summerry Is For
        </h2>
        <p class="mb-4 text-gray-700 dark:text-gray-300">Summerry may be a good fit if you:</p>
        <ul class="mb-4 ml-6 list-disc text-gray-700 dark:text-gray-300">
          <li>work with AI but don't have time to read every article,</li>
          <li>follow the technology and machine learning sectors,</li>
          <li>prefer structured, low-noise information,</li>
          <li>want to stay informed without feeling overwhelmed.</li>
        </ul>
      </section>

      <section aria-labelledby="contact-us">
        <h2 id="contact-us" class="mb-3 mt-6 text-xl font-semibold text-gray-900 dark:text-gray-100">Contact Us</h2>
        <p class="text-gray-700 dark:text-gray-300">
          Questions, suggestions, or ideas? Write to us at:
          <a
            href="mailto:info.summerry@gmail.com"
            class="underline px-0 text-base text-gray-900 transition-colors hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-400"
            >info.summerry&#64;gmail.com</a
          >.
        </p>
        <p class="mt-2 text-gray-700 dark:text-gray-300">
          We're open to feedback as we continue improving the system — let us know what works and what doesn't.
        </p>
      </section>
    </div>
  </div>
</div>
